import React, { useState } from 'react';
import {
  Chip, Stack, Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
} from '@mui/material';
import { BRAND } from '../theme/tokens';
import { BTN_STANDARD } from '../theme/poButtonTokens';
import type { ImportLineState, ImportUserAction } from '../utils/codeMatching';

const STATUS_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  matched: { bg: '#e8f5e9', color: '#2e7d32', label: 'Matched' },
  suggested: { bg: '#fff8e1', color: '#f57f17', label: 'Suggested' },
  new: { bg: '#f5f5f5', color: '#616161', label: 'New' },
  service: { bg: '#eceff1', color: '#546e7a', label: 'Service' },
};

export const ImportResolutionBadge: React.FC<{ state: ImportLineState }> = ({ state }) => {
  const s = STATUS_STYLE[state.status] ?? STATUS_STYLE.new;
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{ height: 18, fontSize: '0.58rem', fontWeight: 800, backgroundColor: s.bg, color: s.color }}
    />
  );
};

// Compact standard tint — modal table row actions (no fills; budget on footer).
const secondaryBtnSx = (compact?: boolean) => ({
  ...BTN_STANDARD,
  fontSize: compact ? '0.64rem' : '0.7rem',
  height: compact ? 24 : 28,
  py: 0.2,
  px: 1,
  minWidth: 0,
  lineHeight: 1.4,
  whiteSpace: 'nowrap' as const,
});

const mutedBtnSx = (compact?: boolean) => ({
  ...secondaryBtnSx(compact),
  color: '#546e7a',
  border: '1px solid #e0e0e0',
  '&:hover': { borderColor: '#546e7a', color: '#37474f', backgroundColor: '#f4f4f5', boxShadow: 'none' },
});

/** Batch toolbar button with the same outlined treatment. */
export const PreviewBatchButton: React.FC<{
  label: string;
  onClick: () => void;
  muted?: boolean;
}> = ({ label, onClick, muted }) => (
  <Button size="small" variant="contained" disableElevation onClick={onClick}
    sx={muted ? mutedBtnSx() : secondaryBtnSx()}>
    {label}
  </Button>
);

// ── InfoableButton (v1.22) — apply-tooltip-pattern for buttons ───────────────
// Wraps any button; on hover a small `?` glyph appears at the top-right
// corner. Clicking the `?` (not the button itself) opens an explanation
// dialog with Description and Example sections.

export interface ButtonInfo {
  title: string;
  description: string;
  example: string;
}

export const InfoableButton: React.FC<{
  info: ButtonInfo;
  children: React.ReactNode;
}> = ({ info, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Box sx={{
        position: 'relative', display: 'inline-flex',
        '&:hover .btn-info-glyph': { opacity: 1 },
      }}>
        {children}
        <Box
          className="btn-info-glyph"
          onClick={e => { e.stopPropagation(); setOpen(true); }}
          sx={{
            position: 'absolute', top: -7, right: -7, zIndex: 2,
            width: 16, height: 16, borderRadius: '50%',
            backgroundColor: '#fff', border: '1.5px solid #bbb', color: '#777',
            fontSize: '0.6rem', fontWeight: 800, lineHeight: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'help', opacity: 0, transition: 'opacity 120ms ease',
            '&:hover': { borderColor: BRAND, color: BRAND, backgroundColor: '#f7f3ff' },
          }}>
          ?
        </Box>
      </Box>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1 }}>{info.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.4 }}>
            Description
          </Typography>
          <Typography sx={{ fontSize: '0.84rem', color: '#333', mb: 2 }}>{info.description}</Typography>
          <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.4 }}>
            Example
          </Typography>
          <Typography sx={{ fontSize: '0.84rem', color: '#333', backgroundColor: '#faf8ff', border: `1px solid ${BRAND}22`, borderRadius: 1, p: 1.2 }}>
            {info.example}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 700, color: BRAND }}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const ImportLineActions: React.FC<{
  state: ImportLineState;
  onAction: (action: ImportUserAction) => void;
  compact?: boolean;
}> = ({ state, onAction, compact }) => {
  if (state.status === 'matched') return null;
  if (state.status === 'suggested') {
    return (
      <Button size="small" variant="contained" disableElevation
        onClick={() => onAction('confirmSuggested')} sx={secondaryBtnSx(compact)}>
        Confirm
      </Button>
    );
  }
  if (state.status === 'new') {
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="contained" disableElevation
          onClick={() => onAction('autoCreate')} sx={secondaryBtnSx(compact)}>
          Auto-create
        </Button>
        <Button size="small" variant="contained" disableElevation
          onClick={() => onAction('service')} sx={mutedBtnSx(compact)}>
          Service
        </Button>
      </Stack>
    );
  }
  return null;
};
